/**
 * Auth context — hydrates the current user from `GET /auth/me` when a token
 * exists in localStorage, exposes `login/signup/logout/refreshMe` and reacts
 * to `wc:auth-expired` events from the API client.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  apiGet,
  apiPost,
  clearToken,
  getToken,
  setToken,
} from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refreshMe = useCallback(async () => {
    if (!getToken()) {
      if (mounted.current) {
        setUser(null);
        setLoading(false);
      }
      return null;
    }
    try {
      const me = await apiGet("/auth/me");
      if (mounted.current) {
        setUser(me);
        setLoading(false);
      }
      return me;
    } catch (e) {
      if (mounted.current) {
        setUser(null);
        setLoading(false);
      }
      return null;
    }
  }, []);

  // Hydrate on mount.
  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  // Listen for global auth-expired broadcasts (from api.js on any 401).
  useEffect(() => {
    const onExpired = () => {
      clearToken();
      if (mounted.current) setUser(null);
    };
    window.addEventListener("wc:auth-expired", onExpired);
    return () => window.removeEventListener("wc:auth-expired", onExpired);
  }, []);

  const login = useCallback(async ({ email, password }) => {
    const data = await apiPost("/auth/login", { email, password }, { auth: false });
    if (data?.token) setToken(data.token);
    if (mounted.current) setUser(data?.user || null);
    return data;
  }, []);

  const signup = useCallback(
    async ({ email, password, full_name, niche }) => {
      const body = { email, password, full_name };
      if (niche) body.niche = niche;
      const data = await apiPost("/auth/signup", body, { auth: false });
      if (data?.token) setToken(data.token);
      if (mounted.current) setUser(data?.user || null);
      return data;
    },
    []
  );

  const logout = useCallback(() => {
    clearToken();
    if (mounted.current) setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      token: getToken(),
      loading,
      login,
      signup,
      logout,
      refreshMe,
      isAuthenticated: Boolean(user),
    }),
    [user, loading, login, signup, logout, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth() must be used inside <AuthProvider>");
  }
  return ctx;
}
